import { Empty, Table, Tag } from "antd";
import React from "react";
import { useMetricPerformanceContext } from "@src/contexts/ab-testing/metric-performance-context";
import { useGetGeneralField } from "@src/hooks/ab-testing/use-get-general-field";
import { SpotLightEnum } from "@src/pages/ab-test-explore/enum";
import { ABExplorationKeyWords } from "@src/constant/ab-testing/ab-testing-exploration.enum";
import { toTitle } from "@src/helpers/text-helper";
import { css } from "@emotion/react";
import { useChartWrapperContext } from "@src/contexts/ab-testing/chart-wrapper-context";
import { ControlReport, VariantReport } from "./variant-report";
import { useGetMetricApplied } from "@src/hooks/fb-explore/metric-performance/use-get-metric-applied";
import { isBoolean, uniqBy } from "lodash";

function getObjectMergedKey(r: any, keys?: string[]) {
    return keys?.map((k) => r[k]).join("-") || "";
}

export const ReportSummary: React.FC<{
    targetKey: string;
    chartKey: string;
    loading?: boolean;
    customTableProps?: any;
    reportSpotLight: SpotLightEnum;
}> = ({ targetKey, chartKey, loading = false, customTableProps, reportSpotLight }) => {
    const {
        dataSources: { [chartKey]: rawData },
    } = useChartWrapperContext();
    const { form } = useMetricPerformanceContext();
    const { variants, variants_options, baseVariant } = useGetGeneralField({ form });
    const { significanceLevel, breakdownBy, measureType, measureValue, measureName } = useGetMetricApplied({
        targetKey,
    });

    console.log({ measureValue, measureName });
    const mergedDetailReport = React.useMemo(() => {
        if (rawData === undefined) return [];
        const groupedData = Object.groupBy(rawData, (i) => getObjectMergedKey(i, breakdownBy));

        return Object.keys(groupedData).map((batchId) => {
            const records: any[] = groupedData[batchId] || [];
            return {
                batchId,
                batchIdentifiers:
                    breakdownBy
                        ?.map((k: any) => {
                            if (records === undefined || records.length === 0) return {};

                            return {
                                [k]: records[0][k],
                            };
                        })
                        .reduce((prev: any, curr: any) => Object.assign(prev, curr), {}) || {},
                // variants: records
                variants: records
                    .map((rec) => {
                        return {
                            [rec[ABExplorationKeyWords.VARIANT]]: {
                                ...rec,
                            },
                        };
                    })
                    .reduce((prev, curr) => Object.assign(prev, curr)),
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify({ rawData, breakdownBy })]);

    const detailTableColumns = React.useMemo(() => {
        const _detailTableColumns = [];
        const cacheBaseVariant: Record<string, any> = {};

        if (breakdownBy !== undefined && breakdownBy.length > 0) {
            _detailTableColumns.push({
                title: "",
                children: [
                    ...breakdownBy.map((v: string) => {
                        return {
                            title: toTitle(v),
                            width: 120,
                            fixed: "left",
                            dataIndex: ["batchIdentifiers", v],
                            render: (value: any[]) => {
                                if (isBoolean(value)) {
                                    return (
                                        <div className="pl-[8px]">
                                            {value ? <Tag color="green">TRUE</Tag> : <Tag color="red">FALSE</Tag>}
                                        </div>
                                    );
                                }
                                return <div className="pl-[8px]">{value || value === 0 ? value : <Tag>NULL</Tag>}</div>;
                            },
                            filters: uniqBy(
                                mergedDetailReport.map(({ batchIdentifiers }) => {
                                    const { [v]: distinctValue } = batchIdentifiers;
                                    return {
                                        text: distinctValue !== null ? distinctValue : "NULL",
                                        value: distinctValue !== null ? distinctValue : "NULL",
                                    };
                                }),
                                (item) => item.value,
                            ).sort(
                                (
                                    a: { text: string; value: number | string },
                                    b: { text: string; value: number | string },
                                ) => {
                                    const v1 = a.value === "NULL" ? -1 : a.value;
                                    const v2 = b.value === "NULL" ? -1 : b.value;
                                    if (typeof v1 === "number" && typeof v2 === "number") {
                                        return v1 - v2;
                                    }
                                    return v1?.toString()?.localeCompare(v2?.toString());
                                },
                            ),
                            onFilter: (value: any, record: any) => {
                                const _record =
                                    record["batchIdentifiers"][v] === null ? "NULL" : record["batchIdentifiers"][v];
                                if (_record === "NULL") return _record === value;
                                if (typeof _record === "number" || typeof _record === "string")
                                    return _record === value;
                                return _record?.includes(value);
                            },
                            sorter: (a: any, b: any) => {
                                const batchIdentifiersA = a["batchIdentifiers"][v];
                                const batchIdentifiersB = b["batchIdentifiers"][v];
                                const v1: number = batchIdentifiersA === null ? -1 : batchIdentifiersA;
                                const v2: number = batchIdentifiersB === null ? -1 : batchIdentifiersB;
                                if (typeof v1 === "number" && typeof v2 === "number") {
                                    return v1 - v2;
                                } else {
                                    return (batchIdentifiersA || "").localeCompare(batchIdentifiersB || "");
                                }
                            },
                        };
                    }),
                ],
            });
        }
        _detailTableColumns.push({
            title: "Variants",
            children: [
                ...(variants
                    ?.sort((a: string, b: string) => (a === baseVariant ? -1 : b === baseVariant ? 1 : 0))
                    .map((v: string) => {
                        const { label: groupAlias } = variants_options?.find(({ value }) => value === v) || {};
                        return {
                            title: groupAlias,
                            key: v,
                            width: 200,
                            dataIndex: ["variants", v],
                            render: (value: any, record: any) => {
                                const k = record.batchId + "-" + baseVariant;
                                if (v === baseVariant) {
                                    cacheBaseVariant[k] = value;
                                    return (
                                        <ControlReport
                                            measureName={measureValue}
                                            reportSpotLight={reportSpotLight}
                                            measureType={measureType}
                                            groupName={v}
                                            groupAlias={groupAlias || ""}
                                            report={value}
                                        />
                                    );
                                }
                                return (
                                    <VariantReport
                                        measureName={measureValue}
                                        baseVariantReport={cacheBaseVariant[k]}
                                        reportSpotLight={reportSpotLight}
                                        measureType={measureType}
                                        groupName={v}
                                        groupAlias={groupAlias || ""}
                                        report={value}
                                        isSignificantFn={(pValue) => {
                                            return pValue !== null && pValue !== undefined
                                                ? pValue < significanceLevel
                                                : false;
                                        }}
                                        colorFn={(pValue, upliftPercent) => {
                                            const color = {
                                                bg: "bg-neutral-100",
                                                upliftPercentTextColor: "text-slate-900",
                                            };
                                            if (pValue === undefined || !upliftPercent) {
                                                return color;
                                            }

                                            if (pValue < significanceLevel) {
                                                color.bg = upliftPercent > 0 ? "bg-green-100" : "bg-red-100";
                                                color.upliftPercentTextColor =
                                                    upliftPercent > 0 ? "text-green-500" : "text-red-500";
                                            }
                                            return color;
                                        }}
                                    />
                                );
                            },
                        };
                    }) || []),
            ],
        });
        return _detailTableColumns;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variants, breakdownBy]);

    return mergedDetailReport === undefined || mergedDetailReport.length === 0 ? (
        <Empty description={"No data"} />
    ) : (
        <Table
            rowKey="batchId"
            sticky={{
                offsetHeader: 0,
            }}
            columns={detailTableColumns}
            dataSource={mergedDetailReport}
            bordered
            size="middle"
            pagination={{
                defaultPageSize: 50,
                hideOnSinglePage: true,
            }}
            css={css`
                /* .ant-table-tbody{
                padding: 0 !important;
            } */
                .ant-table-tbody > tr > td {
                    padding: 0 !important;
                }
            `}
            scroll={{ x: "max-content" }}
            loading={loading}
            {...customTableProps}
        />
    );
};
